import type { Problem } from '@arcnet/contracts';

/**
 * The API client.
 *
 * Two things here are load-bearing and easy to get wrong:
 *
 * 1. The access token lives in MEMORY ONLY -- never localStorage. An XSS that
 *    can read localStorage owns the session for its full lifetime; a token held
 *    in a module closure dies with the tab. The refresh token is in an HttpOnly
 *    cookie the browser attaches automatically and JS cannot read at all.
 *
 * 2. Refresh is SINGLE-FLIGHT. On a cold load the app fires several requests at
 *    once; if the token has expired they all 401 together. Without
 *    de-duplication each would trigger its own refresh, and because refresh
 *    tokens ROTATE, the second one to land would present an already-used token
 *    and trip server-side reuse detection -- logging the user out for doing
 *    nothing wrong. One in-flight refresh, everyone waits for it, all replay.
 */

const BASE = '/api';

let accessToken: string | null = null;
let refreshInFlight: Promise<boolean> | null = null;

type Listener = (authenticated: boolean) => void;
const listeners = new Set<Listener>();

export const onAuthChange = (fn: Listener): (() => void) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};
const emit = (v: boolean) => listeners.forEach((l) => l(v));

export const setAccessToken = (t: string | null): void => { accessToken = t; };
export const hasAccessToken = (): boolean => accessToken !== null;
/** For the SSE stream, which builds its own fetch rather than going through api.*. */
export const getAccessToken = (): string | null => accessToken;

export class ApiClientError extends Error {
  readonly status: number;
  readonly problem: Problem | null;
  constructor(status: number, problem: Problem | null, fallback: string) {
    super(problem?.detail ?? problem?.title ?? fallback);
    this.name = 'ApiClientError';
    this.status = status;
    this.problem = problem;
  }
  get code(): string { return this.problem?.code ?? 'unknown'; }
  /** Field errors from a 422, keyed by field name, for form display. */
  get fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const e of this.problem?.errors ?? []) out[e.field] = e.message;
    return out;
  }
}

async function refreshAccessToken(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    try {
      const res = await fetch(`${BASE}/v1/auth/refresh`, {
        method: 'POST',
        credentials: 'include', // the HttpOnly refresh cookie
      });
      if (!res.ok) { accessToken = null; emit(false); return false; }
      const body = (await res.json()) as { access_token: string };
      accessToken = body.access_token;
      emit(true);
      return true;
    } catch {
      accessToken = null;
      emit(false);
      return false;
    } finally {
      // Cleared in a microtask so concurrent callers already awaiting this
      // promise still observe the same one.
      queueMicrotask(() => { refreshInFlight = null; });
    }
  })();

  return refreshInFlight;
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /** Internal: prevents infinite recursion after a refresh attempt. */
  _retried?: boolean | undefined;
}

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  // `body` and `_retried` are destructured out so neither reaches fetch's
  // RequestInit -- `body: unknown` is not assignable to BodyInit, and the
  // serialised form is added back explicitly below.
  const { body: rawBody, _retried, ...init } = opts;

  const headers = new Headers(init.headers);
  if (rawBody !== undefined) headers.set('Content-Type', 'application/json');
  if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`);

  const res = await fetch(BASE + path, {
    ...init,
    headers,
    credentials: 'include',
    ...(rawBody === undefined ? {} : { body: JSON.stringify(rawBody) }),
  });

  if (res.status === 401 && !_retried && !path.startsWith('/v1/auth/refresh')) {
    const ok = await refreshAccessToken();
    if (ok) return request<T>(path, { ...opts, _retried: true });
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const body: unknown = text ? JSON.parse(text) : null;

  if (!res.ok) {
    if (res.status === 401) { accessToken = null; emit(false); }
    throw new ApiClientError(res.status, body as Problem, res.statusText);
  }
  return body as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body: unknown) => request<T>(path, { method: 'PATCH', body }),
  put: <T>(path: string, body: unknown) => request<T>(path, { method: 'PUT', body }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};

/** Called once at startup: an existing refresh cookie silently restores the session. */
export async function bootstrapSession(): Promise<boolean> {
  return refreshAccessToken();
}

export async function login(email: string, password: string): Promise<void> {
  const res = await fetch(`${BASE}/v1/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ email, password }),
  });
  const text = await res.text();
  const body: unknown = text ? JSON.parse(text) : null;
  if (!res.ok) throw new ApiClientError(res.status, body as Problem, 'Login failed');
  accessToken = (body as { access_token: string }).access_token;
  emit(true);
}

export async function logout(): Promise<void> {
  try { await api.post('/v1/auth/logout'); } catch { /* best effort */ }
  accessToken = null;
  emit(false);
}
